import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnDestroy
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { VarDirective } from '@backoffice-frontend/shared/directives/common';
import { ActionButtonModule } from '@clean-code/shared/components/ui-action-button';
import { ConfirmService } from '@clean-code/shared/components/ui-confirm';
import { IDashboardWidgetDetailDto } from '@clean-code/shared/components/ui-dashboard';
import {
  LoadingIndicatorComponent,
  ProgressBarComponent,
} from '@clean-code/shared/components/ui-progress-bar';
import { DashboardFacade } from '@clean-code/shared/dashboard/dashboard/domain';
import {
  DashboardWidgetAddDialogComponent,
  DashboardWidgetReferenceDialogComponent,
} from '@clean-code/shared/dashboard/widget/ui-widget-dialog';
import { faPlus, faShare } from '@fortawesome/pro-solid-svg-icons';
import { Subject, takeUntil } from 'rxjs';
import { DashboardPanelWidgetsComponent } from '../dashboard-panel-widgets/dashboard-panel-widgets.component';

@Component({
  selector: 'dashboard-dashboard-panel-component',
  templateUrl: './dashboard-panel.component.html',
  styleUrls: ['./dashboard-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ActionButtonModule,
    VarDirective,
    DashboardPanelWidgetsComponent,
    ProgressBarComponent,
    LoadingIndicatorComponent,
  ],
})
export class DashboardPanelComponent implements OnDestroy {
  @HostBinding('class.page-component') pageComponent = true;

  public faPlus = faPlus;
  public faShare = faShare;

  private closeSubject = new Subject<void>();

  constructor(
    private dashboardFacade: DashboardFacade,
    protected activatedRoute: ActivatedRoute,
    private confirmService: ConfirmService,
  ) {
    this.activatedRoute.params.pipe(takeUntil(this.closeSubject)).subscribe((params) => {
      this.dashboardFacade.getById$(+params.id);
    });
  }

  public get editMode() {
    return this.dashboardFacade.editMode;
  }

  public get allowedEdit() {
    return this.dashboardFacade.allowedEdit$;
  }

  public get dashboard() {
    return this.dashboardFacade.active;
  }

  public get hasUserPermission() {
    return this.dashboardFacade.hasUserPermission;
  }

  public get isLoading() {
    return this.dashboardFacade.isLoading;
  }

  ngOnDestroy(): void {
    this.closeSubject.next();
    this.closeSubject.complete();
  }

  public addWidget() {
    if (!this.dashboardFacade.hasUserPermission()) {
      return;
    }

    this.confirmService
      .open(
        '',
        '',
        DashboardWidgetAddDialogComponent,
        { dashboardId: this.dashboard().id, tenant: this.dashboard().tenant },
        {
          width: '800px',
          height: '634px',
        },
      )
      .subscribe((result) => {
        if (result) {
          const widgetWithDashboardId = {
            ...result,
            dashboardId: this.dashboard().id,
            x: result.x ?? 0,
            y: result.y ?? 0,
            cols: result.cols ?? 5,
            rows: result.rows ?? 5,
          } as IDashboardWidgetDetailDto;

          this.dashboardFacade.addWidget(widgetWithDashboardId);
        }
      });
  }

  public referenceWidget() {
    if (!this.dashboardFacade.hasUserPermission()) {
      return;
    }

    const inputWidget = {
      dashboardId: this.dashboard().id,
    } as IDashboardWidgetDetailDto;
    this.confirmService
      .open('', '', DashboardWidgetReferenceDialogComponent, inputWidget, {
        width: '800px',
        height: '634px',
      })
      .subscribe((result) => {
        if (result) {
          const widgetWithDashboardId = {
            ...result,
            dashboardId: this.dashboard().id,
            x: result.x ?? 0,
            y: result.y ?? 0,
            cols: result.cols ?? 5,
            rows: result.rows ?? 5,
          } as IDashboardWidgetDetailDto;
          this.dashboardFacade.addWidget(widgetWithDashboardId);
        }
      });
  }

  public edit() {
    this.dashboardFacade.toggleEdit();
  }
}
